import {
  PortalUsersQuery,
  usePortalUserCreateMutation,
  usePortalUsersQuery,
  usePortalUserUpdateMutation,
} from "@dashboard/graphql";
import { Box, Button, Checkbox, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import { ErrorText, Page, Section } from "../components";
import { portalMessages as m } from "../messages";
import { formatDateTime, today } from "../utils";

type PortalUserRow = PortalUsersQuery["portalUsers"][number];

const COLUMNS =
  "minmax(180px, 2fr) minmax(140px, 1.4fr) minmax(90px, 0.8fr) minmax(150px, 1.4fr) 90px";

/** One account, read-only until "Edit": then role, active and password
 * change together on Save (nothing is applied by a stray click). */
const UserRow = ({
  user,
  roleOptions,
  onChanged,
}: {
  user: PortalUserRow;
  roleOptions: Option[];
  onChanged: () => void;
}) => {
  const intl = useIntl();
  const [form, setForm] = useState<{ roleId: string; isActive: boolean; password: string } | null>(
    null,
  );
  const [update, opts] = usePortalUserUpdateMutation({
    onCompleted: data => {
      if (!data.portalUserUpdate?.errors.length) {
        setForm(null);
        onChanged();
      }
    },
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      paddingY={2}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderColor="default1"
    >
      <Box display="grid" gap={3} alignItems="center" __gridTemplateColumns={COLUMNS}>
        <Box __minWidth={0}>
          <Text size={3} fontWeight="bold" color={user.isActive ? undefined : "default2"}>
            {user.fullName}
          </Text>
          <Text size={1} color="default2" display="block" ellipsis>
            {user.username || user.email}
          </Text>
        </Box>
        <Text size={2}>
          {user.roles.join(", ") || "—"}
          {!user.manageable && (
            <Text size={1} color="default2" display="block">
              {intl.formatMessage(m.managedInDashboard)}
            </Text>
          )}
        </Text>
        <Text size={2} color={user.isActive ? "success1" : "critical1"}>
          {intl.formatMessage(user.isActive ? m.active : m.inactive)}
        </Text>
        <Text size={2} color="default2">
          {user.lastLogin
            ? `${formatDateTime(user.lastLogin)}${
                user.lastLoginPlatform ? ` · ${user.lastLoginPlatform}` : ""
              }`
            : intl.formatMessage(m.never)}
        </Text>
        <Box display="flex" justifyContent="flex-end">
          {user.manageable && !form && (
            <Button
              size="small"
              variant="secondary"
              onClick={() =>
                setForm({ roleId: user.roleId ?? "", isActive: user.isActive, password: "" })
              }
            >
              {intl.formatMessage(m.edit)}
            </Button>
          )}
        </Box>
      </Box>
      {form && (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          padding={3}
          borderRadius={3}
          backgroundColor="default2"
        >
          <Box
            display="grid"
            gap={2}
            alignItems="end"
            __gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          >
            <Select
              size="small"
              label={intl.formatMessage(m.role)}
              options={roleOptions}
              value={roleOptions.find(o => o.value === form.roleId) ?? null}
              onChange={option => setForm({ ...form, roleId: option.value })}
            />
            <Input
              size="small"
              type="password"
              autoComplete="new-password"
              label={intl.formatMessage(m.passwordOptional)}
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
            />
            <Checkbox
              checked={form.isActive}
              onCheckedChange={checked => setForm({ ...form, isActive: checked === true })}
            >
              <Text size={2}>{intl.formatMessage(m.active)}</Text>
            </Checkbox>
          </Box>
          {form.password && (
            <Text size={1} color="default2">
              {intl.formatMessage(m.newPasswordHint)}
            </Text>
          )}
          <ErrorText errors={opts.data?.portalUserUpdate?.errors} />
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button size="small" variant="secondary" onClick={() => setForm(null)}>
              {intl.formatMessage(m.cancel)}
            </Button>
            <Button
              size="small"
              variant="primary"
              disabled={opts.loading}
              onClick={() =>
                update({
                  variables: {
                    id: user.id,
                    roleId: form.roleId !== (user.roleId ?? "") ? form.roleId : undefined,
                    isActive: form.isActive !== user.isActive ? form.isActive : undefined,
                    password: form.password || undefined,
                  },
                })
              }
            >
              {intl.formatMessage(m.save)}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const EMPTY = {
  fullName: "",
  username: "",
  password: "",
  roleId: "",
  hireDate: today(),
  personalEmail: "",
};

export const Users = () => {
  const intl = useIntl();
  const { data, refetch } = usePortalUsersQuery({ fetchPolicy: "cache-and-network" });
  const [form, setForm] = useState<typeof EMPTY | null>(null);
  const [create, createOpts] = usePortalUserCreateMutation({
    onCompleted: result => {
      if (!result.portalUserCreate?.errors.length) {
        setForm(null);
        refetch();
      }
    },
  });
  const roleOptions: Option[] = (data?.portalRoles ?? []).map(role => ({
    value: role.id,
    label: role.name,
  }));

  return (
    <Page>
      <Section
        title={intl.formatMessage(m.users)}
        actions={
          !form && (
            <Button size="small" variant="primary" onClick={() => setForm(EMPTY)}>
              {intl.formatMessage(m.newUserButton)}
            </Button>
          )
        }
      >
        {form && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            padding={3}
            borderRadius={3}
            backgroundColor="default2"
          >
            <Text size={4} fontWeight="bold">
              {intl.formatMessage(m.newUser)}
            </Text>
            <Box
              display="grid"
              gap={2}
              __gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
            >
              <Input
                size="small"
                label={intl.formatMessage(m.name)}
                value={form.fullName}
                maxLength={256}
                onChange={e => setForm({ ...form, fullName: e.target.value })}
              />
              <Input
                size="small"
                label={intl.formatMessage(m.usernameLabel)}
                value={form.username}
                maxLength={150}
                autoComplete="off"
                onChange={e => setForm({ ...form, username: e.target.value.toLowerCase() })}
              />
              <Input
                size="small"
                type="password"
                label={intl.formatMessage(m.initialPassword)}
                value={form.password}
                autoComplete="new-password"
                onChange={e => setForm({ ...form, password: e.target.value })}
              />
              <Select
                size="small"
                label={intl.formatMessage(m.role)}
                options={roleOptions}
                value={roleOptions.find(o => o.value === form.roleId) ?? null}
                onChange={option => setForm({ ...form, roleId: option.value })}
              />
              <Input
                size="small"
                type="date"
                label={intl.formatMessage(m.hireDateLabel)}
                value={form.hireDate}
                onChange={e => setForm({ ...form, hireDate: e.target.value })}
              />
              <Input
                size="small"
                type="email"
                label={intl.formatMessage(m.personalEmailLabel)}
                value={form.personalEmail}
                onChange={e => setForm({ ...form, personalEmail: e.target.value })}
              />
            </Box>
            <Text size={1} color="default2">
              {intl.formatMessage(m.newUserHint)}
            </Text>
            <ErrorText errors={createOpts.data?.portalUserCreate?.errors} />
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button size="small" variant="secondary" onClick={() => setForm(null)}>
                {intl.formatMessage(m.cancel)}
              </Button>
              <Button
                size="small"
                variant="primary"
                disabled={
                  !form.fullName.trim() ||
                  !form.username.trim() ||
                  !form.password ||
                  !form.roleId ||
                  createOpts.loading
                }
                onClick={() => create({ variables: { input: form } })}
              >
                {intl.formatMessage(m.createUser)}
              </Button>
            </Box>
          </Box>
        )}
        <Box __overflowX="auto">
          <Box __minWidth="720px">
            <Box
              display="grid"
              gap={3}
              paddingBottom={1}
              borderBottomWidth={1}
              borderBottomStyle="solid"
              borderColor="default1"
              __gridTemplateColumns={COLUMNS}
            >
              {[m.name, m.role, m.status, m.lastLoginColumn].map(header => (
                <Text key={header.id} size={1} color="default2" fontWeight="bold">
                  {intl.formatMessage(header)}
                </Text>
              ))}
            </Box>
            {(data?.portalUsers ?? []).map(user => (
              <UserRow key={user.id} user={user} roleOptions={roleOptions} onChanged={refetch} />
            ))}
          </Box>
        </Box>
      </Section>
    </Page>
  );
};
